import { useRef, useEffect, useMemo } from "react"
import { Popover } from "react-tiny-popover"
import ChevronDownFill from "~icons/eva/chevron-down-fill"
import ChevronUpFill from "~icons/eva/chevron-up-fill"
import CloseFill from "~icons/eva/close-fill"
import styles from "./styles.module.scss"
import classNames from "classnames"
import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import CheckmarkFill from "~icons/eva/checkmark-fill"
import { DefaultSpinner } from "../../loaders/DefaultSpinner"
import { DefaultInput } from "../DefaultInput"

type SelectOption = { value: string; label: string }
type OptionsArray = SelectOption[]
type NestedOptions = { label: string; options: OptionsArray }[]

interface FormMultiSelectInputProps {
  value: Array<{ id: string | number; name: string }>
  textValue?: string
  onChange: (value: Array<{ id: string | number; name: string }>) => void
  options: OptionsArray | NestedOptions
  label: string
  handleChange?: (value?: string[], label?: string[]) => void
  handlePopoverChange?: (visible: boolean) => void
  isLoading?: boolean
  testID?: string
  darkTheme?: boolean
  searchLabel?: string
  name: string
  parentBackgroundColor: string
  lightOutline?: boolean
}

class FormMultiSelectInputStore {
  isPopoverOpen: boolean
  containerWidth: number | null
  searchTerm: string
  highlightedIndex: number

  constructor() {
    this.isPopoverOpen = false
    this.containerWidth = null
    this.searchTerm = ""
    this.highlightedIndex = -1

    makeAutoObservable(this)
  }

  setIsPopoverOpen(isOpen: boolean) {
    this.isPopoverOpen = isOpen
    if (!isOpen) {
      this.highlightedIndex = -1
    }
  }

  setContainerWidth(width: number) {
    this.containerWidth = width
  }

  setSearchTerm(term: string) {
    this.searchTerm = term
  }

  setHighlightedIndex(index: number) {
    this.highlightedIndex = index
  }
}

const SelectOption = observer(
  ({
    option,
    isSelected,
    isHighlighted,
    onClick,
    testID,
    optionId,
    refCallback,
  }: {
    option: SelectOption
    isSelected: boolean
    isHighlighted: boolean
    onClick: () => void
    testID?: string
    optionId: string
    refCallback: (el: HTMLDivElement | null) => void
  }) => {
    return (
      <div
        ref={refCallback}
        className={classNames(
          styles.option,
          isSelected && styles.selectedOption,
          isHighlighted && styles.highlightedOption,
        )}
        onClick={onClick}
        data-testid={`${testID}-option-${option.label.toLowerCase().replace(" ", "-")}`}
        id={optionId}
      >
        <div
          className={classNames(
            styles.optionCheckbox,
            isSelected ? styles.optionCheckboxActive : styles.optionCheckboxInactive,
          )}
          id={`${optionId}-checkbox`}
        >
          <CheckmarkFill
            id={`${optionId}-checkbox-icon`}
            className={classNames(styles.optionCheckboxIcon)}
          />
        </div>
        <p className={styles.optionText} id={`${optionId}-text`}>
          {option.label}
        </p>
      </div>
    )
  },
)

export const MultiSelectInput = observer(
  ({
    value,
    textValue = "",
    onChange,
    options = [],
    label,
    handleChange,
    handlePopoverChange,
    isLoading = false,
    testID,
    darkTheme = false,
    searchLabel,
    name,
    parentBackgroundColor,
    lightOutline,
  }: FormMultiSelectInputProps) => {
    const store = useMemo(() => new FormMultiSelectInputStore(), [])
    const ref = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const optionRefs = useRef<(HTMLDivElement | null)[]>([])

    useEffect(() => {
      if (ref.current) {
        store.setContainerWidth(ref.current.offsetWidth)
      }
    }, [ref.current])

    const selectedValue =
      value?.length > 0 ? value.map((v) => ({ value: v.id, label: v.name })) : []

    const handleSelectChange = (selectedOption: SelectOption) => {
      const existingValueIndex = value.findIndex((v) => String(v.id) === selectedOption.value)
      let updatedValues
      if (existingValueIndex !== -1) {
        updatedValues = [...value]
        updatedValues.splice(existingValueIndex, 1)
      } else {
        updatedValues = [...value, { id: selectedOption.value, name: selectedOption.label }]
      }
      onChange(updatedValues)
      if (handleChange)
        handleChange(
          updatedValues.map((v) => v.id),
          updatedValues.map((v) => v.name),
        )
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }

    useEffect(() => {
      if (handlePopoverChange) handlePopoverChange(store.isPopoverOpen)
    }, [store.isPopoverOpen])

    const filteredOptions = useMemo(() => {
      const searchLower = store.searchTerm.toLowerCase()
      return options
        .map((option) => {
          if ("options" in option) {
            return {
              ...option,
              options: option.options.filter((subOption) =>
                subOption.label.toLowerCase().includes(searchLower),
              ),
            }
          }
          return option
        })
        .filter((option) => {
          if ("options" in option) {
            return option.options.length > 0
          }
          return option.label.toLowerCase().includes(searchLower)
        })
    }, [options, store.searchTerm])

    useEffect(() => {
      if (!store.isPopoverOpen) {
        store.setSearchTerm("")
      }
    }, [store.isPopoverOpen])

    useEffect(() => {
      if (store.searchTerm.length > 0) {
        store.setHighlightedIndex(0)
      } else {
        store.setHighlightedIndex(-1)
      }
    }, [store.searchTerm])

    const flattenedOptions = useMemo(() => {
      const flattened: SelectOption[] = []
      filteredOptions.forEach((option) => {
        if ("options" in option) {
          option.options.forEach((subOption) => {
            flattened.push(subOption)
          })
        } else {
          flattened.push(option)
        }
      })
      return flattened
    }, [filteredOptions])

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !store.isPopoverOpen) {
        e.preventDefault()
        store.setIsPopoverOpen(true)
        return
      }

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          if (flattenedOptions.length > 0) {
            const nextIndex =
              store.highlightedIndex < flattenedOptions.length - 1 ? store.highlightedIndex + 1 : 0
            store.setHighlightedIndex(nextIndex)
            optionRefs.current[nextIndex]?.scrollIntoView({ block: "nearest" })
          }
          break
        case "ArrowUp":
          e.preventDefault()
          if (flattenedOptions.length > 0) {
            const prevIndex =
              store.highlightedIndex > 0 ? store.highlightedIndex - 1 : flattenedOptions.length - 1
            store.setHighlightedIndex(prevIndex)
            optionRefs.current[prevIndex]?.scrollIntoView({ block: "nearest" })
          }
          break
        case "Enter":
          e.preventDefault()
          if (store.highlightedIndex >= 0 && store.highlightedIndex < flattenedOptions.length) {
            handleSelectChange(flattenedOptions[store.highlightedIndex])
          }
          break
        case "Escape":
          e.preventDefault()
          store.setIsPopoverOpen(false)
          break
        case "Tab":
          if (store.isPopoverOpen) {
            e.preventDefault()
          }
          break
      }
    }

    const indexMap = useMemo(() => {
      let currentIndex = 0
      const map = new Map()

      filteredOptions.forEach((option, groupIndex) => {
        if ("options" in option) {
          option.options.forEach((_, subIndex) => {
            map.set(`${groupIndex}-${subIndex}`, currentIndex++)
          })
        } else {
          map.set(groupIndex.toString(), currentIndex++)
        }
      })

      return map
    }, [filteredOptions])

    useEffect(() => {
      optionRefs.current = optionRefs.current.slice(0, flattenedOptions.length)
    }, [flattenedOptions])

    return (
      <Popover
        isOpen={store.isPopoverOpen}
        positions={["bottom"]}
        padding={5}
        ref={ref}
        onClickOutside={() => store.setIsPopoverOpen(false)}
        containerClassName={styles.popoverContainer}
        content={
          <div
            className={classNames(
              styles.optionsContainer,
              darkTheme && styles.optionsContainerDarkTheme,
            )}
            style={{ width: store.containerWidth || 300 }}
            id="option-list-container"
          >
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <DefaultSpinner size="sm" />
              </div>
            ) : (
              <div
                className={classNames(styles.optionList, darkTheme && styles.optionListDarkTheme)}
                id="option-list"
              >
                {filteredOptions.length === 0 ? (
                  <div className={styles.noOptions} id="option-empty">
                    No options found
                  </div>
                ) : (
                  (() => {
                    return filteredOptions.map((option, index) => {
                      if ("options" in option) {
                        return (
                          <div key={index}>
                            <div className={styles.sectionTitle}>
                              {String(option.label).toUpperCase()}
                            </div>
                            {option.options.map((subOption, subIndex) => {
                              const isSelected = selectedValue.some(
                                (value) => String(value.value) === subOption.value,
                              )
                              const currentOptionIndex = indexMap.get(`${index}-${subIndex}`)
                              const isHighlighted = currentOptionIndex === store.highlightedIndex

                              return (
                                <SelectOption
                                  key={`${index}-${subIndex}`}
                                  option={subOption}
                                  isSelected={isSelected}
                                  isHighlighted={isHighlighted}
                                  onClick={() => handleSelectChange(subOption)}
                                  testID={testID}
                                  optionId={`option-${index}`}
                                  refCallback={(el) =>
                                    (optionRefs.current[currentOptionIndex] = el)
                                  }
                                />
                              )
                            })}
                          </div>
                        )
                      } else {
                        const isSelected = selectedValue.some(
                          (value) => String(value.value) === option.value,
                        )
                        const currentOptionIndex = indexMap.get(index.toString())
                        const isHighlighted = currentOptionIndex === store.highlightedIndex

                        return (
                          <SelectOption
                            key={index}
                            option={option}
                            isSelected={isSelected}
                            isHighlighted={isHighlighted}
                            onClick={() => handleSelectChange(option)}
                            testID={testID}
                            optionId={`option-${index}`}
                            refCallback={(el) => (optionRefs.current[currentOptionIndex] = el)}
                          />
                        )
                      }
                    })
                  })()
                )}
              </div>
            )}
          </div>
        }
      >
        <div className={styles.defaultInputContainer}>
          <DefaultInput
            innerRef={inputRef}
            type="text"
            label={store.isPopoverOpen && searchLabel ? searchLabel : label}
            value={store.isPopoverOpen ? store.searchTerm : textValue}
            onChange={(value) => store.setSearchTerm(value)}
            onClick={() => store.setIsPopoverOpen(!store.isPopoverOpen)}
            onKeyDown={handleKeyDown}
            testId={testID}
            name={name}
            parentBackgroundColor={parentBackgroundColor}
            lightOutline={lightOutline}
            isModalOpen={store.isPopoverOpen}
            className={styles.doubleIconRight}
            iconRight={
              <div className={styles.iconsContainer}>
                {value?.length > 0 && !store.isPopoverOpen ? (
                  <button
                    className={styles.clearButton}
                    type="button"
                    onClick={() => onChange([])}
                    tabIndex={-1}
                  >
                    <CloseFill className={styles.clearButtonIcon} />
                  </button>
                ) : null}
                <button
                  className={styles.arrowButton}
                  onClick={() => store.setIsPopoverOpen(!store.isPopoverOpen)}
                  tabIndex={-1}
                >
                  {store.isPopoverOpen ? (
                    <ChevronUpFill className={styles.arrow} />
                  ) : (
                    <ChevronDownFill className={styles.arrow} />
                  )}
                </button>
              </div>
            }
          />
        </div>
      </Popover>
    )
  },
)
